import React, { useState, useRef, useEffect } from 'react';
import { ContentRow, NavLinkConfig, AccountMenuItemConfig } from '../../../types/homepage';

export interface BlockTheme {
    primaryColor?: string;
    primaryText?: string;
    surfaceColor?: string;
    textColor?: string;
    mutedColor?: string;
    borderColor?: string;
    fontFamily?: string;
}

const DEFAULT_THEME: Required<BlockTheme> = {
    primaryColor: '#00897b',
    primaryText: '#ffffff',
    surfaceColor: '#ffffff',
    textColor: '#111827',
    mutedColor: '#6b7280',
    borderColor: '#e5e7eb',
    fontFamily: "'DM Sans', sans-serif",
};

function useThemeCss(theme: BlockTheme | undefined, prefix: string): string {
    const t = { ...DEFAULT_THEME, ...theme };
    return `
    .${prefix} {
      --primary: ${t.primaryColor};
      --primary-t: ${t.primaryText};
      --surface: ${t.surfaceColor};
      --text: ${t.textColor};
      --muted: ${t.mutedColor};
      --border: ${t.borderColor};
      --font: ${t.fontFamily};
      font-family: ${t.fontFamily};
    }
  `;
}

function getInitials(name: string): string {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return '?';
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

interface HeaderBlockProps {
    row: ContentRow;
    theme?: BlockTheme;
    onSearch?: (query: string) => void;
    onNavClick?: (url: string) => void;
    onLogout?: () => void;
}

export const HeaderBlock: React.FC<HeaderBlockProps> = ({ row, theme, onSearch, onNavClick, onLogout }) => {
    const { settings } = row;
    const css = useThemeCss(theme, 'hdr-root');
    const layout = row.layoutStyle || 'default';
    const [query, setQuery] = useState('');
    const [accountOpen, setAccountOpen] = useState(false);
    const accountRef = useRef<HTMLDivElement>(null);
    const [openNavKey, setOpenNavKey] = useState<string | null>(null);
    const navRef = useRef<HTMLDivElement>(null);

    const logoText = (settings?.logoText as string) || '';
    const logoImage = (settings?.logoImage as string) || '';
    const logoUrl = (settings?.logoUrl as string) || '/';
    const navLinks = (settings?.navLinks as NavLinkConfig[]) || [];
    const topBarLinks = (settings?.topBarLinks as NavLinkConfig[]) || [];
    const showSearch = (settings?.showSearchInHeader as boolean) ?? true;
    const searchPlaceholder = (settings?.searchPlaceholder as string) || 'Search...';
    const bgColor = (settings?.backgroundColor as string) || '#ffffff';
    const textColor = (settings?.textColor as string) || '#111827';
    const topBarBg = (settings?.topBarBgColor as string) || '#00897b';
    const topBarText = (settings?.topBarTextColor as string) || '#ffffff';

    const primaryLabel = settings?.ctaLabel as string;
    const primaryUrl = (settings?.ctaUrl as string) || '#';
    const primaryBg = (settings?.ctaBgColor as string) || '#ffffff';
    const primaryText = (settings?.ctaTextColor as string) || '#00897b';

    const secondaryLabel = settings?.ctaSecondaryLabel as string;
    const secondaryUrl = (settings?.ctaSecondaryUrl as string) || '#';
    const secondaryBg = (settings?.ctaSecondaryBgColor as string) || '#7c3aed';
    const secondaryText = (settings?.ctaSecondaryTextColor as string) || '#ffffff';

    const showAccountMenu = (settings?.showAccountMenu as boolean) ?? false;
    const accountAvatarType = (settings?.accountAvatarType as 'initials' | 'image') || 'initials';
    const accountAvatarImage = (settings?.accountAvatarImage as string) || '';
    const accountAvatarBgColor = (settings?.accountAvatarBgColor as string) || '#e5e7eb';
    const accountAvatarTextColor = (settings?.accountAvatarTextColor as string) || '#111827';
    const accountName = (settings?.accountName as string) || 'User';
    const accountEmail = (settings?.accountEmail as string) || '';
    const accountMenuItems = (settings?.accountMenuItems as AccountMenuItemConfig[]) || [];
    const showLogoutInMenu = (settings?.showLogoutInMenu as boolean) ?? true;
    const logoutLabel = (settings?.logoutLabel as string) || 'Log out';
    const accountMenuBgColor = (settings?.accountMenuBgColor as string) || '#ffffff';
    const accountMenuTextColor = (settings?.accountMenuTextColor as string) || '#111827';

    const handleSearchSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        onSearch?.(query);
    };

    const handleLinkClick = (e: React.MouseEvent, url: string) => {
        if (onNavClick) {
            e.preventDefault();
            onNavClick(url);
        }
        setAccountOpen(false);
        setOpenNavKey(null);
    };

    useEffect(() => {
        if (!accountOpen) return;
        const handleClick = (e: MouseEvent) => {
            if (accountRef.current && !accountRef.current.contains(e.target as Node)) {
                setAccountOpen(false);
            }
        };
        const handleKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setAccountOpen(false);
        };
        document.addEventListener('mousedown', handleClick);
        document.addEventListener('keydown', handleKey);
        return () => {
            document.removeEventListener('mousedown', handleClick);
            document.removeEventListener('keydown', handleKey);
        };
    }, [accountOpen]);


    useEffect(() => {
        if (!openNavKey) return;
        const handleClick = (e: MouseEvent) => {
            if (navRef.current && !navRef.current.contains(e.target as Node)) {
                setOpenNavKey(null);
            }
        };
        const handleKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setOpenNavKey(null);
        };
        document.addEventListener('mousedown', handleClick);
        document.addEventListener('keydown', handleKey);
        return () => {
            document.removeEventListener('mousedown', handleClick);
            document.removeEventListener('keydown', handleKey);
        };
    }, [openNavKey]);

    const renderNavTree = (items: NavLinkConfig[], depth: number): React.ReactNode =>
        items.map((item) => (
            <React.Fragment key={item.key}>
                <a
                    className="hdr-nav-drop-item"
                    href={item.url || '#'}
                    onClick={(e) => handleLinkClick(e, item.url)}
                    style={{ paddingLeft: 16 + depth * 16 }}
                >
                    {depth > 0 ? `- ${item.label}` : item.label}
                </a>
                {item.children && item.children.length > 0 && renderNavTree(item.children, depth + 1)}
            </React.Fragment>
        ));

    return (
        <>
            <style>{`
                ${css}
                .hdr-root.hdr-topbar {
                    background: ${topBarBg}; color: ${topBarText};
                    display: flex; justify-content: flex-end; align-items: center;
                    gap: 22px; padding: 8px 24px; font-size: 12.5px; font-weight: 600;
                }
                .hdr-topbar a { color: ${topBarText}; text-decoration: none; opacity: 0.92; }
                .hdr-topbar a:hover { opacity: 1; text-decoration: underline; }
                .hdr-root.hdr-main {
                    background: ${bgColor}; color: ${textColor};
                    display: flex; align-items: center; gap: 24px;
                    padding: 14px 24px; border-bottom: 1px solid var(--border);
                    flex-wrap: ${layout === 'centered' ? 'wrap' : 'nowrap'};
                    justify-content: ${layout === 'centered' ? 'center' : 'flex-start'};
                }
                .hdr-logo { display: flex; align-items: center; gap: 8px; flex-shrink: 0; text-decoration: none; }
                .hdr-logo img { height: 30px; display: block; }
                .hdr-logo-text { font-weight: 800; font-size: 18px; color: ${textColor}; font-family: var(--font); }
                .hdr-nav { display: flex; align-items: center; gap: 22px; flex-wrap: wrap; }
                .hdr-nav a { color: ${textColor}; text-decoration: none; font-size: 14px; font-weight: 600; white-space: nowrap; }
                .hdr-nav a:hover { color: var(--primary); }
                .hdr-nav-item { position: relative; display: flex; align-items: center; }
                .hdr-nav-btn {
                    display: flex; align-items: center; gap: 5px; background: none; border: none; padding: 0;
                    color: ${textColor}; font-size: 14px; font-weight: 600; font-family: var(--font);
                    white-space: nowrap; cursor: pointer;
                }
                .hdr-nav-btn:hover { color: var(--primary); }
                .hdr-nav-caret { width: 9px; height: 9px; flex-shrink: 0; transition: transform .15s; }
                .hdr-nav-item.open .hdr-nav-caret { transform: rotate(180deg); }
                .hdr-nav-dropdown {
                    position: absolute; top: calc(100% + 14px); left: 0; z-index: 55;
                    min-width: 220px; max-height: 340px; overflow-y: auto;
                    background: var(--surface); border: 1px solid var(--border);
                    border-radius: 8px; box-shadow: 0 10px 30px rgba(0,0,0,0.14);
                    padding: 8px 0; display: flex; flex-direction: column;
                }
                .hdr-nav-drop-item {
                    display: block; padding: 8px 16px; font-size: 13px; font-weight: 600;
                    color: var(--text); text-decoration: none; white-space: nowrap;
                }
                .hdr-nav-drop-item:hover { background: rgba(0,0,0,0.04); color: var(--text); }
                .hdr-spacer { flex: 1; }
                .hdr-right { display: flex; align-items: center; gap: 12px; flex-shrink: 0; }
                .hdr-search { position: relative; }
                .hdr-search input {
                    width: 200px; padding: 8px 34px 8px 12px; border-radius: 6px;
                    border: 1px solid var(--border); font-size: 13px; outline: none;
                    font-family: var(--font); background: #f9fafb;
                }
                .hdr-search button { position: absolute; right: 8px; top: 50%; transform: translateY(-50%); background: none; border: none; cursor: pointer; color: var(--muted); }
                .hdr-search svg { width: 14px; height: 14px; }
                .hdr-btn-primary {
                    padding: 9px 18px; border-radius: 6px; border: 1.5px solid ${primaryText};
                    background: ${primaryBg}; color: ${primaryText}; font-size: 13px; font-weight: 700;
                    text-decoration: none; white-space: nowrap; cursor: pointer;
                }
                .hdr-btn-secondary {
                    padding: 9px 18px; border-radius: 6px; border: none;
                    background: ${secondaryBg}; color: ${secondaryText}; font-size: 13px; font-weight: 700;
                    text-decoration: none; white-space: nowrap; cursor: pointer;
                }
                .hdr-account { position: relative; }
                .hdr-avatar-btn {
                    width: 36px; height: 36px; border-radius: 8px; flex-shrink: 0;
                    display: flex; align-items: center; justify-content: center;
                    background: ${accountAvatarType === 'image' ? 'transparent' : accountAvatarBgColor};
                    color: ${accountAvatarTextColor};
                    border: 1px solid var(--border); cursor: pointer; padding: 0; overflow: hidden;
                    font-size: 12.5px; font-weight: 700; font-family: var(--font);
                }
                .hdr-avatar-btn img { width: 100%; height: 100%; object-fit: cover; display: block; }
                .hdr-account-pop {
                    position: absolute; top: calc(100% + 10px); right: 0; z-index: 60;
                    width: 260px; background: ${accountMenuBgColor}; color: ${accountMenuTextColor};
                    border-radius: 10px; box-shadow: 0 10px 34px rgba(0,0,0,0.16);
                    border: 1px solid var(--border); overflow: hidden; font-family: var(--font);
                }
                .hdr-account-head { display: flex; align-items: center; gap: 10px; padding: 16px; }
                .hdr-account-avatar-lg {
                    width: 46px; height: 46px; border-radius: 50%; flex-shrink: 0; overflow: hidden;
                    display: flex; align-items: center; justify-content: center;
                    background: ${accountAvatarType === 'image' ? 'transparent' : accountAvatarBgColor};
                    color: ${accountAvatarTextColor}; font-size: 15px; font-weight: 700;
                }
                .hdr-account-avatar-lg img { width: 100%; height: 100%; object-fit: cover; display: block; }
                .hdr-account-name { font-size: 14px; font-weight: 700; color: ${accountMenuTextColor}; margin: 0; }
                .hdr-account-email { font-size: 12px; color: var(--muted); margin: 2px 0 0; }
                .hdr-account-list { padding: 6px 0; border-top: 1px solid var(--border); }
                .hdr-account-item {
                    display: flex; align-items: center; gap: 10px; padding: 9px 16px;
                    color: ${accountMenuTextColor}; text-decoration: none; font-size: 13.5px; font-weight: 500;
                    background: none; border: none; width: 100%; text-align: left; cursor: pointer;
                }
                .hdr-account-item:hover { background: rgba(0,0,0,0.04); }
                .hdr-account-icon { font-size: 14px; width: 18px; text-align: center; flex-shrink: 0; }
                .hdr-account-logout-row {
                    display: flex; justify-content: flex-end; padding: 10px 16px;
                    border-top: 1px solid var(--border);
                }
                .hdr-account-logout-row button {
                    background: none; border: none; color: ${accountMenuTextColor}; font-size: 13px;
                    font-weight: 600; cursor: pointer; padding: 4px 2px;
                }
                .hdr-account-logout-row button:hover { text-decoration: underline; }
                @media (max-width: 860px) {
                    .hdr-nav { display: none; }
                    .hdr-search input { width: 130px; }
                }
            `}</style>

            {topBarLinks.length > 0 && (
                <div className="hdr-root hdr-topbar">
                    {topBarLinks.map((l) => (
                        <a key={l.key} href={l.url || '#'} onClick={(e) => handleLinkClick(e, l.url)}>
                            {l.label}
                        </a>
                    ))}
                </div>
            )}

            <div className="hdr-root hdr-main">
                <a className="hdr-logo" href={logoUrl}>
                    {logoImage ? (
                        <img src={logoImage} alt={logoText || 'Logo'} />
                    ) : (
                        <span className="hdr-logo-text">{logoText || 'Logo'}</span>
                    )}
                </a>

                <nav className="hdr-nav" ref={navRef}>
                    {navLinks.map((l) => {
                        const hasChildren = !!l.children && l.children.length > 0;
                        if (!hasChildren) {
                            return (
                                <a key={l.key} href={l.url || '#'} onClick={(e) => handleLinkClick(e, l.url)}>
                                    {l.label}
                                </a>
                            );
                        }
                        const isOpen = openNavKey === l.key;
                        return (
                            <div key={l.key} className={`hdr-nav-item${isOpen ? ' open' : ''}`}>
                                <button
                                    type="button"
                                    className="hdr-nav-btn"
                                    aria-haspopup="menu"
                                    aria-expanded={isOpen}
                                    onClick={() => setOpenNavKey(isOpen ? null : l.key)}
                                >
                                    {l.label}
                                    <svg className="hdr-nav-caret" viewBox="0 0 12 12" fill="none" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M2.5 4.5L6 8l3.5-3.5" />
                                    </svg>
                                </button>
                                {isOpen && (
                                    <div className="hdr-nav-dropdown" role="menu">
                                        {renderNavTree(l.children as NavLinkConfig[], 0)}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </nav>

                <div className="hdr-spacer" />

                <div className="hdr-right">
                    {showSearch && (
                        <form className="hdr-search" onSubmit={handleSearchSubmit}>
                            <input
                                type="text"
                                placeholder={searchPlaceholder}
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                aria-label="Search"
                            />
                            <button type="submit" aria-label="Submit search">
                                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                        d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
                                </svg>
                            </button>
                        </form>
                    )}
                    {primaryLabel && <a className="hdr-btn-primary" href={primaryUrl}>{primaryLabel}</a>}
                    {secondaryLabel && <a className="hdr-btn-secondary" href={secondaryUrl}>{secondaryLabel}</a>}

                    {showAccountMenu && (
                        <div className="hdr-account" ref={accountRef}>
                            <button
                                type="button"
                                className="hdr-avatar-btn"
                                onClick={() => setAccountOpen((o) => !o)}
                                aria-haspopup="menu"
                                aria-expanded={accountOpen}
                                aria-label="Account menu"
                            >
                                {accountAvatarType === 'image' && accountAvatarImage ? (
                                    <img src={accountAvatarImage} alt={accountName} />
                                ) : (
                                    getInitials(accountName)
                                )}
                            </button>

                            {accountOpen && (
                                <div className="hdr-account-pop" role="menu">
                                    <div className="hdr-account-head">
                                        <div className="hdr-account-avatar-lg">
                                            {accountAvatarType === 'image' && accountAvatarImage ? (
                                                <img src={accountAvatarImage} alt={accountName} />
                                            ) : (
                                                getInitials(accountName)
                                            )}
                                        </div>
                                        <div>
                                            <p className="hdr-account-name">{accountName}</p>
                                            {accountEmail && <p className="hdr-account-email">{accountEmail}</p>}
                                        </div>
                                    </div>

                                    {accountMenuItems.length > 0 && (
                                        <div className="hdr-account-list">
                                            {accountMenuItems.map((item) => (
                                                <a
                                                    key={item.key}
                                                    className="hdr-account-item"
                                                    href={item.url || '#'}
                                                    onClick={(e) => handleLinkClick(e, item.url)}
                                                    role="menuitem"
                                                >
                                                    <span className="hdr-account-icon">{item.icon}</span>
                                                    <span>{item.label}</span>
                                                </a>
                                            ))}
                                        </div>
                                    )}

                                    {showLogoutInMenu && (
                                        <div className="hdr-account-logout-row">
                                            <button
                                                type="button"
                                                onClick={() => { setAccountOpen(false); onLogout?.(); }}
                                            >
                                                {logoutLabel}
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export default HeaderBlock;